import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  FormControlLabel,
  Switch,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  AttachFile as AttachIcon,
  Preview as PreviewIcon,
  Code as CodeIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import emailTemplatesApi, { EmailTemplate, EmailTemplateAttachment } from '../../services/email-templates-api';

// System variables that are automatically available
const SYSTEM_VARIABLES = [
  { name: 'contact.name', description: "Contact's full name" },
  { name: 'contact.first_name', description: "Contact's first name" },
  { name: 'contact.last_name', description: "Contact's last name" },
  { name: 'contact.email', description: "Contact's email" },
  { name: 'contact.phone', description: "Contact's phone" },
  { name: 'contact.company', description: "Contact's company" },
  { name: 'contact.city', description: "Contact's city" },
  { name: 'contact.country', description: "Contact's country" },
  { name: 'account.name', description: 'Your company name' },
  { name: 'agent.name', description: 'Sending agent name' },
  { name: 'agent.email', description: 'Sending agent email' },
  { name: 'current_date', description: 'Current date' },
  { name: 'current_time', description: 'Current time' },
];

const CATEGORIES = [
  { value: 'marketing', label: 'Marketing' },
  { value: 'transactional', label: 'Transactional' },
  { value: 'support', label: 'Support' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'notification', label: 'Notification' },
  { value: 'other', label: 'Other' },
];

interface Props {
  template?: EmailTemplate | null;
  onSave: () => void;
  onCancel: () => void;
}

const EmailTemplateBuilder: React.FC<Props> = ({ template, onSave, onCancel }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [name, setName] = useState(template?.name || '');
  const [subject, setSubject] = useState(template?.subject || '');
  const [bodyHtml, setBodyHtml] = useState(template?.body_html || '');
  const [bodyText, setBodyText] = useState(template?.body_text || '');
  const [category, setCategory] = useState(template?.category || '');
  const [signatureEnabled, setSignatureEnabled] = useState(template?.signature_enabled ?? true);
  const [customSignature, setCustomSignature] = useState(template?.custom_signature || '');
  const [customVariables, setCustomVariables] = useState<string[]>(
    (template?.variables || []).filter(v => !SYSTEM_VARIABLES.find(sv => sv.name === v))
  );
  const [attachments, setAttachments] = useState<EmailTemplateAttachment[]>(template?.attachments || []);
  const [newVariable, setNewVariable] = useState('');
  const [isActive, setIsActive] = useState(template?.is_active ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState(false);

  const allVariables = [
    ...SYSTEM_VARIABLES.map(v => v.name),
    ...customVariables
  ];

  const handleAddVariable = () => {
    const trimmed = newVariable.trim().toLowerCase().replace(/\s+/g, '_');
    if (trimmed && !allVariables.includes(trimmed)) {
      setCustomVariables([...customVariables, trimmed]);
      setNewVariable('');
    }
  };

  const handleRemoveVariable = (variable: string) => {
    setCustomVariables(customVariables.filter(v => v !== variable));
  };

  const insertVariable = (variable: string) => {
    const placeholder = `{{${variable}}}`;
    if (activeTab === 0) {
      setBodyHtml(bodyHtml + placeholder);
    } else {
      setSubject(subject + placeholder);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // In real implementation, upload to server and get URL
    // For now, create a placeholder
    const newAttachment: EmailTemplateAttachment = {
      filename: file.name,
      file_url: URL.createObjectURL(file), // Replace with actual upload URL
      file_size: file.size,
      mime_type: file.type,
      is_inline: false,
    };

    setAttachments([...attachments, newAttachment]);
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!name.trim() || !subject.trim() || !bodyHtml.trim()) {
      setError('Name, subject, and body are required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const data = {
        name: name.trim(),
        subject: subject.trim(),
        body_html: bodyHtml,
        body_text: bodyText || undefined,
        category: category || undefined,
        signature_enabled: signatureEnabled,
        custom_signature: customSignature || undefined,
        variables: [...SYSTEM_VARIABLES.map(v => v.name), ...customVariables],
        attachments,
        is_active: isActive,
      };

      if (template) {
        await emailTemplatesApi.update(template.id, data);
      } else {
        await emailTemplatesApi.create(data);
      }

      onSave();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const renderPreview = () => {
    let previewHtml = bodyHtml;
    let previewSubject = subject;

    // Replace variables with sample data
    const sampleData: Record<string, string> = {
      'contact.name': 'John Doe',
      'contact.first_name': 'John',
      'contact.last_name': 'Doe',
      'contact.email': 'john@example.com',
      'contact.phone': '+1234567890',
      'contact.company': 'Acme Inc',
      'contact.city': 'New York',
      'contact.country': 'USA',
      'account.name': 'Your Company',
      'agent.name': 'Support Agent',
      'agent.email': 'agent@company.com',
      'current_date': new Date().toLocaleDateString(),
      'current_time': new Date().toLocaleTimeString(),
    };

    allVariables.forEach(v => {
      const value = sampleData[v] || `[${v}]`;
      previewHtml = previewHtml.replace(new RegExp(`\\{\\{${v}\\}\\}`, 'g'), value);
      previewSubject = previewSubject.replace(new RegExp(`\\{\\{${v}\\}\\}`, 'g'), value);
    });

    return (
      <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Subject:
        </Typography>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {previewSubject}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Box dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml) }} />
        {signatureEnabled && customSignature && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(customSignature) }} />
          </>
        )}
      </Paper>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          {template ? 'Edit Email Template' : 'Create Email Template'}
        </Typography>
        <Box>
          <Button
            variant={previewMode ? 'contained' : 'outlined'}
            startIcon={<PreviewIcon />}
            onClick={() => setPreviewMode(!previewMode)}
            sx={{ mr: 1 }}
          >
            {previewMode ? 'Edit' : 'Preview'}
          </Button>
          <Button onClick={onCancel} sx={{ mr: 1 }}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Template'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {previewMode ? renderPreview() : (
        <Grid container spacing={3}>
          {/* Main Content */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    label="Template Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., welcome_email, order_confirmation"
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={category}
                      label="Category"
                      onChange={(e) => setCategory(e.target.value)}
                    >
                      <MenuItem value="">None</MenuItem>
                      {CATEGORIES.map(cat => (
                        <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <TextField
                fullWidth
                label="Email Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                sx={{ mt: 2 }}
                placeholder="e.g., Welcome {{contact.first_name}}!"
                helperText="Use {{variable_name}} for dynamic content"
              />

              <Box sx={{ mt: 2 }}>
                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
                  <Tab label="HTML Body" icon={<CodeIcon />} iconPosition="start" />
                  <Tab label="Plain Text" />
                </Tabs>

                {activeTab === 0 ? (
                  <TextField
                    fullWidth
                    value={bodyHtml}
                    onChange={(e) => setBodyHtml(e.target.value)}
                    multiline
                    rows={15}
                    sx={{ mt: 1 }}
                    placeholder="<h1>Hello {{contact.first_name}}</h1><p>Welcome to our service!</p>"
                  />
                ) : (
                  <TextField
                    fullWidth
                    value={bodyText}
                    onChange={(e) => setBodyText(e.target.value)}
                    multiline
                    rows={15}
                    sx={{ mt: 1 }}
                    placeholder="Hello {{contact.first_name}}, Welcome to our service!"
                  />
                )}
              </Box>
            </Paper>

            {/* Signature Section */}
            <Paper sx={{ p: 3, mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle1">Email Signature</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={signatureEnabled}
                      onChange={(e) => setSignatureEnabled(e.target.checked)}
                    />
                  }
                  label="Include signature"
                />
              </Box>

              {signatureEnabled && (
                <TextField
                  fullWidth
                  label="Custom Signature (leave empty to use account default)"
                  value={customSignature}
                  onChange={(e) => setCustomSignature(e.target.value)}
                  multiline
                  rows={4}
                  placeholder="<p>Best regards,<br>{{agent.name}}<br>{{account.name}}</p>"
                />
              )}
            </Paper>

            {/* Attachments Section */}
            <Paper sx={{ p: 3, mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle1">Attachments</Typography>
                <Button
                  variant="outlined"
                  startIcon={<AttachIcon />}
                  component="label"
                >
                  Add Attachment
                  <input type="file" hidden onChange={handleFileUpload} />
                </Button>
              </Box>

              {attachments.length > 0 ? (
                <List>
                  {attachments.map((att, index) => (
                    <ListItem key={index}>
                      <AttachIcon sx={{ mr: 1 }} />
                      <ListItemText
                        primary={att.filename}
                        secondary={att.file_size ? `${(att.file_size / 1024).toFixed(1)} KB` : ''}
                      />
                      <ListItemSecondaryAction>
                        <IconButton edge="end" onClick={() => handleRemoveAttachment(index)}>
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary" variant="body2">
                  No attachments. Click "Add Attachment" to include files.
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            {/* Variables */}
            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Dynamic Variables
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Click to insert into template. System variables are auto-filled.
              </Typography>

              <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold' }}>
                System Variables (Auto-filled)
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, mb: 2 }}>
                {SYSTEM_VARIABLES.map((v) => (
                  <Tooltip key={v.name} title={v.description}>
                    <Chip
                      label={v.name}
                      size="small"
                      color="primary"
                      variant="outlined"
                      onClick={() => insertVariable(v.name)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Tooltip>
                ))}
              </Box>

              <Typography variant="caption" color="secondary" sx={{ fontWeight: 'bold' }}>
                Custom Variables
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 1 }}>
                <TextField
                  size="small"
                  placeholder="Variable name"
                  value={newVariable}
                  onChange={(e) => setNewVariable(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddVariable()}
                  fullWidth
                />
                <Button variant="outlined" onClick={handleAddVariable}>
                  <AddIcon />
                </Button>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {customVariables.map((v) => (
                  <Chip
                    key={v}
                    label={v}
                    size="small"
                    color="secondary"
                    onDelete={() => handleRemoveVariable(v)}
                    onClick={() => insertVariable(v)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Paper>

            {/* Settings */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Settings
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                }
                label="Active"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Inactive templates cannot be used for sending
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default EmailTemplateBuilder;
